import React from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, ReferenceLine, ReferenceArea, Label, ComposedChart, Bar } from 'recharts';

/**
 * Custom Y-Axis Tick Formatter for better readability
 */
const formatYAxis = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}k`;
  }
  return `$${value}`;
};

/**
 * Custom Tooltip with milestone information
 */
const CustomTooltip = ({ active, payload, label, milestones }) => {
  if (!active || !payload || payload.length === 0) return null;

  const age = label;
  const milestonesAtAge = milestones.filter(m => m.age === age);

  // Check if this datapoint has portfolio distribution (retirement withdrawal)
  const dataPoint = payload[0]?.payload;
  const hasDistribution = dataPoint?.portfolioDistribution > 0;
  const hasExpenseBreakdown = dataPoint?.expenses && dataPoint?.totalIncome;
  const hasOneTimeExpense = dataPoint?.oneTimeExpenses > 0;
  const netCashFlow = dataPoint?.netCashFlow || 0;
  const portfolioValue = dataPoint?.portfolioValue;

  return (
    <div className="bg-white border-2 border-blue-200 rounded-xl p-3 shadow-xl max-w-xs">
      <p className="font-bold text-gray-900 mb-2">Age {age}</p>
      {payload.map((entry, index) => {
        // Show Total Expenses in red
        const isExpenses = entry.name === 'Total Expenses';
        const displayColor = isExpenses ? '#ef4444' : entry.color;

        return (
          <p key={index} style={{ color: displayColor }} className="text-sm font-medium">
            {entry.name}: <span className="font-semibold">${Math.round(entry.value).toLocaleString()}</span>
          </p>
        );
      })}
      {hasOneTimeExpense && (
        <div className="mt-2 pt-2 border-t border-red-200">
          <p className="text-xs text-red-700 font-bold">
            🔴 One-Time Expense: ${Math.round(dataPoint.oneTimeExpenses).toLocaleString()}
          </p>
        </div>
      )}
      {hasExpenseBreakdown && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <p className={`text-xs font-bold ${netCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            Net Cash Flow: ${Math.round(netCashFlow).toLocaleString()}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            Savings Rate: {dataPoint.totalIncome > 0 ? Math.round((netCashFlow / dataPoint.totalIncome) * 100) : 0}%
          </p>
        </div>
      )}
      {portfolioValue !== undefined && (
        <div className="mt-2 pt-2 border-t border-blue-200 bg-blue-50 -mx-3 -mb-3 px-3 pb-3 rounded-b-lg">
          <p className={`text-sm font-bold ${portfolioValue >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
            💰 Portfolio: ${Math.round(portfolioValue).toLocaleString()}
          </p>
          {portfolioValue < 0 && (
            <p className="text-xs text-red-600 mt-1 font-medium">
              ⚠️ Portfolio depleted - lifestyle may not be sustainable
            </p>
          )}
        </div>
      )}
      {hasDistribution && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <p className="text-xs text-orange-700 font-medium">
            💸 Withdrawing: <span className="font-bold">${Math.round(dataPoint.portfolioDistribution).toLocaleString()}</span>
          </p>
        </div>
      )}
      {milestonesAtAge.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          {milestonesAtAge.map((milestone, idx) => (
            <p key={idx} className="text-xs font-medium" style={{ color: milestone.color }}>
              {milestone.icon} {milestone.label}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Generate milestone markers from vision data
 */
export const getMilestones = (vision) => {
  const milestones = [];

  // Retirement
  if (vision.retireAge) {
    milestones.push({
      age: vision.retireAge,
      label: 'Retirement',
      icon: '🎉',
      color: '#0ea5e9'
    });
  }

  // Home purchase
  if (vision.housingStatus === 'will-buy' && vision.homePurchaseAge) {
    const homePrice = vision.homePrice || 450000;
    const downPayment = vision.downPayment || (homePrice * 0.20);
    milestones.push({
      age: vision.homePurchaseAge,
      label: `Buy Home (-$${Math.round(downPayment / 1000)}k down)`,
      icon: '🏠',
      color: '#3b82f6'
    });
  }

  // Mortgage payoff - for existing homeowners
  if (vision.housingStatus === 'already-own' && vision.yearsLeftOnMortgage > 0) {
    const payoffAge = vision.age + vision.yearsLeftOnMortgage;
    milestones.push({
      age: payoffAge,
      label: 'Mortgage Paid Off',
      icon: '🎊',
      color: '#10b981'
    });
  }

  // Mortgage payoff - for will-buy (30 years after purchase)
  if (vision.housingStatus === 'will-buy' && vision.homePurchaseAge) {
    const payoffAge = vision.homePurchaseAge + 30;
    if (payoffAge <= 95) { // Only show if within lifetime
      milestones.push({
        age: payoffAge,
        label: 'Mortgage Paid Off',
        icon: '🎊',
        color: '#10b981'
      });
    }
  }

  // First child
  if (vision.kids > 0 && vision.firstChildAge) {
    milestones.push({
      age: vision.firstChildAge,
      label: `First Child Born`,
      icon: '👶',
      color: '#14b8a6'
    });
  }

  // Last child
  if (vision.kids > 1 && vision.firstChildAge && vision.childSpacing) {
    const lastChildAge = vision.firstChildAge + (vision.kids - 1) * vision.childSpacing;
    milestones.push({
      age: lastChildAge,
      label: `Last Child Born`,
      icon: '👶',
      color: '#14b8a6'
    });
  }

  // Kids off to college (first child)
  if (vision.kids > 0 && vision.firstChildAge) {
    milestones.push({
      age: vision.firstChildAge + 18,
      label: 'First Child → College',
      icon: '🎓',
      color: '#0ea5e9'
    });
  }

  // Empty nest (last child turns 18)
  if (vision.kids > 0 && vision.firstChildAge && vision.childSpacing) {
    const emptyNestAge = vision.firstChildAge + (vision.kids - 1) * vision.childSpacing + 18;
    milestones.push({
      age: emptyNestAge,
      label: 'Empty Nest',
      icon: '🏡',
      color: '#10b981'
    });
  }

  // Medicare eligibility
  milestones.push({
    age: 65,
    label: 'Medicare Eligible',
    icon: '🏥',
    color: '#06b6d4'
  });

  // Social Security
  milestones.push({
    age: 67,
    label: 'Social Security',
    icon: '💰',
    color: '#14b8a6'
  });

  // Career events
  if (vision.careerTimeline) {
    vision.careerTimeline.forEach(event => {
      if (event.type === 'promotion') {
        milestones.push({
          age: event.age,
          label: 'Promotion',
          icon: '📈',
          color: '#0ea5e9'
        });
      } else if (event.type === 'job_change') {
        milestones.push({
          age: event.age,
          label: 'Job Change',
          icon: '💼',
          color: '#3b82f6'
        });
      } else if (event.type === 'layoff') {
        milestones.push({
          age: event.age,
          label: 'Layoff',
          icon: '⚠️',
          color: '#64748b'
        });
      }
    });
  }

  // One-time events - show ALL events on the chart
  if (vision.oneTimeEvents) {
    vision.oneTimeEvents.forEach(event => {
      // Determine icon and color based on event type or amount
      let icon = '📅';
      let color = '#6366f1';
      let label = event.name || 'Event';

      if (event.type === 'wedding') {
        icon = '💒';
        color = '#14b8a6';
      } else if (event.type === 'inheritance' || event.type === 'windfall') {
        icon = '💸';
        color = '#10b981';
      } else if (event.type === 'car_purchase') {
        icon = '🚗';
        color = '#3b82f6';
      } else if (event.type === 'major_purchase') {
        icon = '🛒';
        color = '#0ea5e9';
      } else if (event.type === 'vacation') {
        icon = '✈️';
        color = '#06b6d4';
      } else {
        // Generic handling - use positive/negative icon
        if (event.amount > 0) {
          icon = '💰'; // Income/windfall
          color = '#10b981';
        } else if (event.amount < 0) {
          icon = '💳'; // Expense
          color = '#64748b';
        }
      }

      milestones.push({
        age: event.age,
        label: label,
        icon: icon,
        color: color
      });
    });
  }

  // Lifestyle expenses - show accepted lifestyle trade-offs on the chart
  if (vision.lifestyleExpenses) {
    vision.lifestyleExpenses.forEach(expense => {
      let icon = expense.isIncomeBoost ? '💰' : '✨';
      let color = expense.isIncomeBoost ? '#10b981' : '#0ea5e9';

      milestones.push({
        age: expense.startAge,
        label: expense.name,
        icon: icon,
        color: color
      });
    });
  }

  return milestones.sort((a, b) => a.age - b.age);
};

/**
 * Improved Income vs Expenses Chart
 */
export const IncomeExpensesChart = ({ data, vision, darkMode }) => {
  const milestones = getMilestones(vision);

  // Show major life milestones + ALL one-time events
  const eventMilestones = milestones.filter(m => {
    // Include major life events
    if (['Retirement', 'Buy Home', 'First Child Born', 'Medicare Eligible'].includes(m.label)) {
      return true;
    }
    // Include all one-time events (they have the custom icons)
    if (vision.oneTimeEvents && vision.oneTimeEvents.some(e => e.age === m.age)) {
      return true;
    }
    return false;
  });

  return (
    <div className={`rounded-xl shadow-lg border p-6 hover:shadow-xl transition-shadow ${
      darkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-blue-100'
    }`}>
      <h3 className={`font-bold mb-4 text-base flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        <span className="text-2xl">💰</span>
        <span>Income vs Expenses</span>
        {vision.hasPartner && <span className={`text-xs ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(includes partner)</span>}
      </h3>
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={data} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="colorWorkIncome" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.3}/>
            </linearGradient>
            <linearGradient id="colorRetirementIncome" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.3}/>
            </linearGradient>
            <linearGradient id="colorWithdrawals" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#14b8a6" stopOpacity={0.3}/>
            </linearGradient>
            <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#14b8a6" stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="age"
            stroke="#6b7280"
            tick={{ fontSize: 12 }}
          />
          <YAxis
            yAxisId="left"
            stroke="#6b7280"
            tickFormatter={formatYAxis}
            tick={{ fontSize: 12 }}
            width={60}
            label={{ value: 'Gross Income/Expenses', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="#10b981"
            tickFormatter={formatYAxis}
            tick={{ fontSize: 12 }}
            width={70}
            label={{ value: 'Portfolio', angle: 90, position: 'insideRight', style: { fontSize: 11, fill: '#10b981' } }}
          />
          <Tooltip content={<CustomTooltip milestones={milestones} />} />

          {/* Event milestone markers */}
          {eventMilestones.map((milestone, idx) => (
            <ReferenceLine
              key={idx}
              x={milestone.age}
              yAxisId="left"
              stroke={milestone.color}
              strokeDasharray="3 3"
              strokeWidth={2}
            >
              <Label
                value={milestone.icon}
                position="top"
                style={{ fontSize: 18 }}
              />
            </ReferenceLine>
          ))}

          {/* Total Income (simplified) - smooth curve for gradual changes */}
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="netIncome"
            name="Total Income (After-Tax)"
            stroke="#3b82f6"
            strokeWidth={3}
            fillOpacity={0.3}
            fill="url(#colorWorkIncome)"
          />

          {/* Portfolio Withdrawals (when negative cash flow) - smooth curve */}
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="portfolioDistribution"
            name="Portfolio Withdrawals"
            stroke="#ef4444"
            strokeWidth={3}
            fillOpacity={0.5}
            fill="#ef4444"
          />

          {/* One-Time Expenses as Vertical Bars */}
          <Bar
            yAxisId="left"
            dataKey="oneTimeExpenses"
            name="One-Time Expenses"
            fill="#dc2626"
            opacity={0.7}
            barSize={8}
          />

          {/* Expenses Line - smooth curve for gradual changes */}
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="expenses"
            name="Total Expenses"
            stroke="#14b8a6"
            strokeWidth={3}
            fillOpacity={0.1}
            fill="url(#colorExpenses)"
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Income vs Expenses Legend */}
      <div className="mt-3 space-y-2">
        <div className="flex flex-wrap gap-3 text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2">
          <div className="flex items-center gap-1">
            <div className="w-4 h-3 bg-blue-500 rounded"></div>
            <span className="text-gray-700 font-medium">Total Income (After-Tax)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-3 bg-red-500 rounded"></div>
            <span className="text-gray-700 font-medium">Portfolio Withdrawals</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-3 border-2 border-teal-500 bg-transparent rounded"></div>
            <span className="text-gray-700 font-medium">Total Expenses</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-4 bg-red-600 rounded-sm"></div>
            <span className="text-gray-700 font-medium">One-Time Expenses</span>
          </div>
        </div>

        {/* Milestone legend */}
        {eventMilestones.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {eventMilestones.slice(0, 6).map((milestone, idx) => (
              <div key={idx} className="flex items-center gap-1 text-xs">
                <span>{milestone.icon}</span>
                <span className="text-gray-600">{milestone.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Improved Portfolio Chart with segmented view
 */
export const PortfolioChart = ({ data, vision, darkMode }) => {
  const milestones = getMilestones(vision);
  const retirementAge = vision.retireAge || 65;

  // Split data into retirement phase
  const retirementYears = data.filter(d => d.age >= retirementAge);

  const majorMilestones = milestones.filter(m =>
    ['Retirement', 'Medicare Eligible', 'Social Security'].includes(m.label)
  );

  // Use the data as-is (removed confusing best/worst case scenarios)
  const scenarioData = data;

  return (
    <div className={`rounded-xl shadow-lg border p-6 hover:shadow-xl transition-shadow ${
      darkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-green-100'
    }`}>
      <h3 className={`font-bold mb-4 text-base flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        <span className="text-2xl">📈</span>
        <span>Portfolio Value Over Time</span>
        <span className={`text-xs font-normal ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(Retire at {retirementAge})</span>
      </h3>
      <ResponsiveContainer width="100%" height={350}>
        <AreaChart data={scenarioData} margin={{ top: 25, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="colorPortfolio" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#10b981" stopOpacity={0.2}/>
            </linearGradient>
            <linearGradient id="colorWithdrawal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.6}/>
              <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="age"
            stroke="#6b7280"
            tick={{ fontSize: 12 }}
          />
          <YAxis
            stroke="#6b7280"
            tickFormatter={formatYAxis}
            tick={{ fontSize: 12 }}
            width={60}
          />

          {/* Retirement phase shading */}
          <ReferenceArea
            x1={retirementAge}
            x2={data[data.length - 1]?.age || 95}
            fill="#fef3c7"
            fillOpacity={0.3}
          />
          <Tooltip content={<CustomTooltip milestones={milestones} />} />

          {/* Retirement line - always show prominently */}
          <ReferenceLine
            x={retirementAge}
            stroke="#f59e0b"
            strokeWidth={3}
            strokeDasharray="5 5"
          >
            <Label
              value={`🎯 Retire ${retirementAge}`}
              position="top"
              style={{ fontSize: 12, fill: '#b45309', fontWeight: 'bold' }}
            />
          </ReferenceLine>

          {/* Other milestone reference lines */}
          {majorMilestones.filter(m => m.label !== 'Retirement').map((milestone, idx) => (
            <ReferenceLine
              key={idx}
              x={milestone.age}
              stroke={milestone.color}
              strokeDasharray="3 3"
              strokeWidth={2}
            >
              <Label
                value={milestone.icon}
                position="top"
                style={{ fontSize: 16 }}
              />
            </ReferenceLine>
          ))}

          {/* Zero line */}
          <ReferenceLine y={0} stroke="#9ca3af" strokeWidth={1} />

          {/* Main Portfolio Line */}
          <Area
            type="monotone"
            dataKey="portfolioValue"
            name="Expected Portfolio"
            stroke="#10b981"
            strokeWidth={3}
            fillOpacity={1}
            fill="url(#colorPortfolio)"
          />
          <Area
            type="monotone"
            dataKey="portfolioDistribution"
            name="Withdrawals"
            stroke="#ef4444"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorWithdrawal)"
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Phase indicator and Scenario Legend */}
      <div className="mt-3 space-y-2">
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1">
            <span className="text-blue-700 font-medium">Accumulation</span>
            <span className="text-blue-600 ml-1">({vision.age}-{retirementAge})</span>
          </div>
          <div className="bg-green-50 border border-green-200 rounded px-2 py-1">
            <span className="text-green-700 font-medium">Distribution</span>
            <span className="text-green-600 ml-1">({retirementAge}+)</span>
            {retirementYears.length > 0 && retirementYears[0]?.portfolioDistribution > 0 && (
              <div className="text-orange-700 font-semibold mt-1">
                💸 ${Math.round(retirementYears[0].portfolioDistribution / 1000)}k/yr
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

/**
 * Improved Stress Chart
 */
export const StressChart = ({ data, vision, darkMode }) => {
  const milestones = getMilestones(vision);

  return (
    <div className={`rounded-xl shadow-lg border p-6 hover:shadow-xl transition-shadow ${
      darkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-purple-100'
    }`}>
      <h3 className={`font-bold mb-4 text-base flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        <span className="text-2xl">😌</span>
        <span>Financial Stress Level</span>
      </h3>
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="colorStress" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.2}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="age"
            stroke="#6b7280"
            tick={{ fontSize: 12 }}
          />
          <YAxis
            domain={[0, 100]}
            stroke="#6b7280"
            tick={{ fontSize: 12 }}
            ticks={[0, 25, 50, 75, 100]}
          />
          <Tooltip content={<CustomTooltip milestones={milestones} />} />

          {/* Stress level zones */}
          <ReferenceLine y={75} stroke="#64748b" strokeDasharray="3 3" label={{ value: "High", position: "right", fontSize: 10 }} />
          <ReferenceLine y={50} stroke="#0ea5e9" strokeDasharray="3 3" label={{ value: "Moderate", position: "right", fontSize: 10 }} />
          <ReferenceLine y={25} stroke="#10b981" strokeDasharray="3 3" label={{ value: "Low", position: "right", fontSize: 10 }} />

          <Area
            type="monotone"
            dataKey="stress"
            name="Stress"
            stroke="#0ea5e9"
            fillOpacity={1}
            fill="url(#colorStress)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Savings Rate Tooltip (shows percentages instead of dollars)
 */
const SavingsRateTooltip = ({ active, payload, label, milestones }) => {
  if (!active || !payload || payload.length === 0) return null;

  const age = label;
  const milestonesAtAge = milestones.filter(m => m.age === age);

  return (
    <div className="bg-white border-2 border-gray-300 rounded-lg p-3 shadow-lg">
      <p className="font-bold text-gray-900 mb-2">Age {age}</p>
      {payload.map((entry, index) => (
        <p key={index} style={{ color: entry.color }} className="text-sm">
          {entry.name}: <span className="font-semibold">{entry.value.toFixed(1)}%</span>
        </p>
      ))}
      {milestonesAtAge.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          {milestonesAtAge.map((milestone, idx) => (
            <p key={idx} className="text-xs text-purple-700 font-medium">
              {milestone.icon} {milestone.label}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Savings Rate Chart (Working Years Only)
 */
export const SavingsRateChart = ({ data, vision, darkMode }) => {
  const milestones = getMilestones(vision);
  const workingYears = data.filter(d => d.age < vision.retireAge);

  return (
    <div className={`rounded-xl shadow-lg border p-6 hover:shadow-xl transition-shadow ${
      darkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-cyan-100'
    }`}>
      <h3 className={`font-bold mb-4 text-base flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        <span className="text-2xl">💎</span>
        <span>Savings Rate</span>
        <span className={`text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>(Working Years)</span>
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={workingYears}>
          <defs>
            <linearGradient id="colorSavingsRate" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#10b981" stopOpacity={0.2}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
          <XAxis
            dataKey="age"
            stroke={darkMode ? '#9ca3af' : '#6b7280'}
            tick={{ fontSize: 12, fill: darkMode ? '#9ca3af' : '#6b7280' }}
          />
          <YAxis
            stroke={darkMode ? '#9ca3af' : '#6b7280'}
            tick={{ fontSize: 12, fill: darkMode ? '#9ca3af' : '#6b7280' }}
            domain={[-20, 60]}
            ticks={[-20, 0, 10, 20, 30, 40, 50, 60]}
            label={{ value: '%', position: 'insideLeft', offset: -5, fill: darkMode ? '#9ca3af' : '#6b7280' }}
          />
          <Tooltip content={<SavingsRateTooltip milestones={milestones} />} />

          {/* Benchmark reference lines */}
          <ReferenceLine y={0} stroke="#9ca3af" strokeWidth={2} label={{ value: "Breaking Even", position: "right", fontSize: 10 }} />
          <ReferenceLine y={15} stroke="#10b981" strokeDasharray="3 3" label={{ value: "Good (15%)", position: "right", fontSize: 10 }} />
          <ReferenceLine y={20} stroke="#059669" strokeDasharray="3 3" label={{ value: "Great (20%)", position: "right", fontSize: 10 }} />
          <ReferenceLine y={30} stroke="#047857" strokeDasharray="3 3" label={{ value: "Excellent (30%)", position: "right", fontSize: 10 }} />

          <Area
            type="monotone"
            dataKey="savingsRate"
            name="Savings Rate"
            stroke="#10b981"
            fillOpacity={1}
            fill="url(#colorSavingsRate)"
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Explanation */}
      <div className={`mt-3 text-xs space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        <p><span className="font-semibold">Savings Rate</span> = (Income - Expenses) ÷ Income</p>
        <div className="flex gap-4">
          <span>• 0-10%: Low</span>
          <span>• 10-20%: Good</span>
          <span>• 20-30%: Great</span>
          <span>• 30%+: Excellent</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Net Worth Chart - Shows Total Assets, Liabilities, and Net Worth over time
 */
export const NetWorthChart = ({ data, vision }) => {
  const milestones = getMilestones(vision);

  const majorMilestones = milestones.filter(m =>
    ['Retirement', 'Medicare Eligible', 'Social Security'].includes(m.label)
  );

  return (
    <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-shadow">
      <h3 className="font-bold text-gray-900 mb-4 text-base flex items-center gap-2">
        <span className="text-2xl">💰</span>
        <span>Net Worth Projection</span>
      </h3>
      <ResponsiveContainer width="100%" height={350}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="colorNetWorth" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
            </linearGradient>
            <linearGradient id="colorAssets" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.6}/>
              <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
            </linearGradient>
            <linearGradient id="colorLiabilities" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.6}/>
              <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="age"
            stroke="#6b7280"
            tick={{ fontSize: 12 }}
          />
          <YAxis
            stroke="#6b7280"
            tickFormatter={formatYAxis}
            tick={{ fontSize: 12 }}
            width={70}
          />
          <Tooltip content={<NetWorthTooltip milestones={milestones} />} />

          {/* Milestone reference lines */}
          {majorMilestones.map((milestone, idx) => (
            <ReferenceLine
              key={idx}
              x={milestone.age}
              stroke={milestone.color}
              strokeDasharray="3 3"
              strokeWidth={2}
            >
              <Label
                value={milestone.icon}
                position="top"
                style={{ fontSize: 16 }}
              />
            </ReferenceLine>
          ))}

          {/* Zero line */}
          <ReferenceLine y={0} stroke="#9ca3af" strokeWidth={2} />

          {/* Debt Paid Off milestone */}
          <ReferenceLine y={0} stroke="#059669" strokeWidth={1} strokeDasharray="5 5" />

          {/* Total Assets (faint background) */}
          <Area
            type="monotone"
            dataKey="totalAssets"
            name="Total Assets"
            stroke="#10b981"
            strokeWidth={1.5}
            strokeDasharray="3 3"
            fill="url(#colorAssets)"
            fillOpacity={0.3}
          />

          {/* Total Liabilities (shown as negative for visual clarity) */}
          <Area
            type="monotone"
            dataKey="totalLiabilities"
            name="Total Debt"
            stroke="#ef4444"
            strokeWidth={1.5}
            strokeDasharray="3 3"
            fill="url(#colorLiabilities)"
            fillOpacity={0.3}
          />

          {/* Net Worth - Main focus line */}
          <Area
            type="monotone"
            dataKey="netWorth"
            name="Net Worth"
            stroke="#3b82f6"
            strokeWidth={4}
            fill="url(#colorNetWorth)"
            fillOpacity={0.4}
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Legend and Summary */}
      <div className="mt-4 space-y-3">
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="bg-green-50 border border-green-200 rounded px-3 py-2">
            <div className="text-green-700 font-medium">Assets</div>
            <div className="text-green-800 font-semibold text-sm mt-1">
              {data.length > 0 ? formatYAxis(data[data.length - 1].totalAssets) : '$0'}
            </div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded px-3 py-2">
            <div className="text-red-700 font-medium">Debt</div>
            <div className="text-red-800 font-semibold text-sm mt-1">
              {data.length > 0 ? formatYAxis(Math.abs(data[data.length - 1].totalLiabilities)) : '$0'}
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2">
            <div className="text-blue-700 font-medium">Net Worth</div>
            <div className="text-blue-800 font-semibold text-sm mt-1">
              {data.length > 0 ? formatYAxis(data[data.length - 1].netWorth) : '$0'}
            </div>
          </div>
        </div>

        {/* Explanation */}
        <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
          <span className="font-semibold">Net Worth</span> = Total Assets (Portfolio + Home Equity + Other) - Total Debt
        </div>
      </div>
    </div>
  );
};

/**
 * Custom Tooltip for Net Worth Chart
 */
const NetWorthTooltip = ({ active, payload, label, milestones }) => {
  if (!active || !payload || payload.length === 0) return null;

  const age = label;
  const milestonesAtAge = milestones.filter(m => m.age === age);
  const dataPoint = payload[0]?.payload;

  return (
    <div className="bg-white border-2 border-blue-200 rounded-xl p-3 shadow-xl max-w-xs">
      <p className="font-bold text-gray-900 mb-2">Age {age}</p>

      <div className="space-y-1">
        <p className="text-sm font-medium text-green-600">
          Assets: <span className="font-bold">${Math.round(dataPoint?.totalAssets || 0).toLocaleString()}</span>
        </p>
        <p className="text-sm font-medium text-red-600">
          Debt: <span className="font-bold">${Math.round(Math.abs(dataPoint?.totalLiabilities || 0)).toLocaleString()}</span>
        </p>
        <div className="pt-2 border-t border-gray-200">
          <p className="text-sm font-bold text-blue-600">
            Net Worth: <span className="text-lg">${Math.round(dataPoint?.netWorth || 0).toLocaleString()}</span>
          </p>
        </div>
      </div>

      {dataPoint?.portfolioValue && (
        <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-600">
          <p>Portfolio: ${Math.round(dataPoint.portfolioValue).toLocaleString()}</p>
          {dataPoint.homeEquity > 0 && (
            <p>Home Equity: ${Math.round(dataPoint.homeEquity).toLocaleString()}</p>
          )}
        </div>
      )}

      {milestonesAtAge.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          {milestonesAtAge.map((milestone, idx) => (
            <p key={idx} className="text-xs font-medium" style={{ color: milestone.color }}>
              {milestone.icon} {milestone.label}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

const ImprovedCharts = { IncomeExpensesChart, PortfolioChart, StressChart, SavingsRateChart, NetWorthChart };
export default ImprovedCharts;
